import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Input, Button, Tabs, message, Tag, Alert, Space, Spin } from 'antd';
import { PlusOutlined, CalendarOutlined, LoadingOutlined } from '@ant-design/icons';
import WorkPlanService from '../services/Workplanservice';
import FacilityService from '../services/FacilityService';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

// ─── Asset type config ────────────────────────────────────────────────────────
const ASSET_TYPES = [
    { value: '2', label: 'Non Medical Assets', icon: '🏥', color: 'blue' },
    { value: '8', label: 'Vehicles',           icon: '🚗', color: 'green' },
    { value: '9', label: 'Supplies',           icon: '📦', color: 'orange' },
    { value: '3', label: 'Medical Assets',     icon: '💊', color: 'red' },
];

// Clickable tile multi-selector for asset types
const AssetTypeSelector = ({ value, onChange }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {ASSET_TYPES.map(type => {
            const selected = Array.isArray(value) && value.includes(type.value);
            return (
                <div
                    key={type.value}
                    onClick={() => {
                        const current = Array.isArray(value) ? value : [];
                        onChange(selected
                            ? current.filter(v => v !== type.value)
                            : [...current, type.value]);
                    }}
                    style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: `2px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                        background: selected ? '#e6f7ff' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: selected ? 600 : 400,
                        color: selected ? '#1890ff' : '#595959',
                        transition: 'all 0.2s',
                        userSelect: 'none',
                    }}
                >
                    <span style={{ fontSize: '16px' }}>{type.icon}</span>
                    <span style={{ fontSize: '13px' }}>{type.label}</span>
                </div>
            );
        })}
    </div>
);

// Helper: render selected asset type tags (used by other components)
export const AssetTypeTags = ({ assetTypes }) => {
    if (!assetTypes || assetTypes.length === 0)
        return <span style={{ color: '#bfbfbf' }}>—</span>;
    const types = Array.isArray(assetTypes)
        ? assetTypes
        : String(assetTypes).split(',').map(s => s.trim());
    return (
        <Space wrap>
            {types.map(v => {
                const t = ASSET_TYPES.find(at => at.value === String(v).trim());
                return t ? <Tag key={v} color={t.color}>{t.icon} {t.label}</Tag> : null;
            })}
        </Space>
    );
};

export { ASSET_TYPES };

/**
 * VisitScheduleForm Component
 * Properly handles Axios response structure (response.data.code instead of response.code)
 */
const VisitScheduleForm = ({
    visible,
    onCancel,
    onSuccess,
    workPlanId,
    workPlanData,
    existingVisits = []
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingFacilities, setLoadingFacilities] = useState(false);
    const [activeTab, setActiveTab] = useState('single');
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [availableFacilities, setAvailableFacilities] = useState([]);

    // Fetch facilities when modal opens
    useEffect(() => {
        if (visible && workPlanData) {
            console.log('Modal opened - fetching facilities for region:', workPlanData.region);
            fetchFacilities();
        }
    }, [visible, workPlanData]);

    const fetchFacilities = async () => {
        setLoadingFacilities(true);
        try {
            console.log('=== FETCH FACILITIES DEBUG ===');
            console.log('1. Work plan region:', workPlanData.region);
            console.log('2. Calling FacilityService.getByRegion...');

            const response = await FacilityService.getByRegion(workPlanData.region);

            console.log('3. Full Axios response:', response);
            console.log('4. Response.data:', response.data);
            console.log('5. Response.data.code:', response.data?.code);
            console.log('6. Response.data.result:', response.data?.result);

            // ⭐ CRITICAL FIX: Axios wraps response in .data property
            // Backend returns: {code: 200, message: "", result: {data: [...]}}
            // Axios returns: {data: {code: 200, message: "", result: {data: [...]}}}

            if (response.data && response.data.code === 200) {
                const facilitiesData = response.data.result?.data || [];
                console.log('7. ✅ Facilities extracted:', facilitiesData.length, 'facilities');
                console.log('8. First facility:', facilitiesData[0]);

                setFacilities(facilitiesData);
                processFacilities(facilitiesData);
            } else {
                console.error('❌ Unexpected response code:', response.data?.code);
                message.error('Failed to load facilities: Invalid response');
                setFacilities([]);
                setAvailableFacilities([]);
            }
        } catch (error) {
            console.error('=== ERROR ===');
            console.error('Error object:', error);
            console.error('Error message:', error.message);
            console.error('Error response:', error.response?.data);

            message.error('Failed to load facilities: ' + (error.message || 'Unknown error'));
            setFacilities([]);
            setAvailableFacilities([]);
        } finally {
            setLoadingFacilities(false);
            console.log('=== FETCH COMPLETE ===');
        }
    };

    const processFacilities = (facilitiesData) => {
        const visits = Array.isArray(existingVisits) ? existingVisits : [];

        console.log('=== PROCESSING FACILITIES ===');
        console.log('Total facilities:', facilitiesData.length);
        console.log('Existing visits:', visits.length);
        console.log('Work plan ID:', workPlanId);

        // Get facility IDs that are already scheduled
        const scheduledFacilityIds = visits
            .filter(v => v.work_plan_id === workPlanId)
            .map(v => v.client_id);

        console.log('Scheduled facility IDs:', scheduledFacilityIds);

        // Add scheduling metadata to each facility
        const processed = facilitiesData.map(facility => {
            const isScheduled = scheduledFacilityIds.includes(facility.id);
            const facilityVisits = visits.filter(v =>
                v.client_id === facility.id &&
                v.work_plan_id === workPlanId
            );

            return {
                ...facility,
                isScheduled,
                scheduleCount: facilityVisits.length,
                scheduledDates: facilityVisits.map(v => v.scheduled_date),
                displayName: isScheduled
                    ? `${facility.business_name} ✓ (${facilityVisits.length} visit(s))`
                    : facility.business_name
            };
        });

        setAvailableFacilities(processed);
        console.log('✅ Available facilities processed:', processed.length);
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setSelectedFacility(null);
            setSelectedFacilities([]);
            setActiveTab('single');
            setFacilities([]);
            setAvailableFacilities([]);
        }
    }, [visible, form]);

    const handleFacilityChange = (facilityId) => {
        const facility = availableFacilities.find(f => f.id === facilityId);
        setSelectedFacility(facility);
    };

    const handleSingleVisit = async (values) => {
        try {
            setLoading(true);

            // Validate asset types
            if (!values.asset_types || values.asset_types.length === 0) {
                message.error('Please select at least one asset type to collect');
                setLoading(false);
                return;
            }

            const visitData = {
                work_plan_id: workPlanId,
                client_id: values.facility,
                scheduled_date: values.date.format('YYYY-MM-DD'),
                day_number: values.day_number || 1,
                status: 'pending',
                notes: values.notes || '',
                asset_types: JSON.stringify(values.asset_types),
                asset_type_labels: values.asset_types
                    .map(v => ASSET_TYPES.find(t => t.value === v)?.label)
                    .filter(Boolean)
                    .join(', ')
            };

            console.log('Scheduling single visit:', visitData);

            await WorkPlanService.scheduleVisit(visitData);

            message.success('Visit scheduled successfully');
            form.resetFields();
            onSuccess();
            onCancel();
        } catch (error) {
            console.error('Error scheduling visit:', error);
            message.error(error.response?.data?.message || 'Failed to schedule visit');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkSchedule = async (values) => {
        try {
            setLoading(true);

            // Validate asset types
            if (!values.bulk_asset_types || values.bulk_asset_types.length === 0) {
                message.error('Please select at least one asset type to collect');
                setLoading(false);
                return;
            }

            const assetTypeLabels = values.bulk_asset_types
                .map(v => ASSET_TYPES.find(t => t.value === v)?.label)
                .filter(Boolean)
                .join(', ');

            const startDate = moment(values.bulk_start_date);
            const schedulePromises = selectedFacilities.map((facilityId, index) => {
                const visitDate = startDate.clone().add(index, 'days');

                return WorkPlanService.scheduleVisit({
                    work_plan_id: workPlanId,
                    client_id: facilityId,
                    scheduled_date: visitDate.format('YYYY-MM-DD'),
                    day_number: index + 1,
                    status: 'pending',
                    notes: values.bulk_notes || '',
                    asset_types: JSON.stringify(values.bulk_asset_types),
                    asset_type_labels: assetTypeLabels
                });
            });

            console.log(`Scheduling ${schedulePromises.length} visits in bulk`);

            await Promise.all(schedulePromises);

            message.success(`${selectedFacilities.length} visits scheduled successfully`);
            form.resetFields();
            setSelectedFacilities([]);
            onSuccess();
            onCancel();
        } catch (error) {
            console.error('Error bulk scheduling:', error);
            message.error(error.response?.data?.message || 'Failed to schedule visits');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        form.validateFields().then(values => {
            if (activeTab === 'single') {
                handleSingleVisit(values);
            } else {
                handleBulkSchedule(values);
            }
        }).catch(errorInfo => {
            console.log('Validation failed:', errorInfo);
        });
    };

    const disabledDate = (current) => {
        return current && current < moment().startOf('day');
    };

    return (
        <Modal
            title={
                <Space>
                    <CalendarOutlined />
                    <span>Schedule Facility Visit</span>
                    {workPlanData && (
                        <Tag color="blue">{workPlanData.name}</Tag>
                    )}
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            onOk={handleSubmit}
            okText={activeTab === 'single' ? 'Schedule Visit' : `Schedule ${selectedFacilities.length} Visits`}
            okButtonProps={{
                loading,
                disabled: (activeTab === 'bulk' && selectedFacilities.length === 0) || loadingFacilities
            }}
            width={700}
            destroyOnClose
        >
            {/* Loading facilities spinner */}
            {loadingFacilities && (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
                        tip="Loading facilities..."
                    />
                </div>
            )}

            {/* Show alerts only after loading completes */}
            {!loadingFacilities && (
                <>
                    {/* No facilities available */}
                    {availableFacilities.length === 0 && (
                        <Alert
                            message="No facilities available"
                            description={
                                facilities.length === 0
                                    ? `No facilities found in ${workPlanData?.region || 'this region'}. Please add facilities first.`
                                    : "All facilities in this region have already been scheduled for this work plan."
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* Some facilities already scheduled */}
                    {availableFacilities.length > 0 && availableFacilities.some(f => f.isScheduled) && (
                        <Alert
                            message="Some facilities already scheduled"
                            description="Facilities marked with ✓ already have visits scheduled. You can still schedule additional visits."
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}
                </>
            )}

            {/* Form content - hidden while loading */}
            {!loadingFacilities && (
                <Tabs activeKey={activeTab} onChange={setActiveTab}>

                    {/* ── SINGLE VISIT TAB ───────────────────────────────── */}
                    <TabPane tab="Single Visit" key="single">
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={{ day_number: 1 }}
                        >
                            {/* ── NEW: Asset Type Selector ── */}
                            <Form.Item
                                label="What are you collecting data for?"
                                name="asset_types"
                                rules={[{ required: true, message: 'Please select at least one asset type' }]}
                                extra="Select all asset types that will be surveyed during this visit"
                            >
                                <AssetTypeSelector
                                    value={form.getFieldValue('asset_types') || []}
                                    onChange={v => form.setFieldsValue({ asset_types: v })}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Select Facility"
                                name="facility"
                                rules={[{ required: true, message: 'Please select a facility' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Search by facility name or code"
                                    onChange={handleFacilityChange}
                                    disabled={availableFacilities.length === 0}
                                    filterOption={(input, option) => {
                                        const facility = availableFacilities.find(f => f.id === option.value);
                                        if (!facility) return false;
                                        const searchText = input.toLowerCase();
                                        return (
                                            facility.business_name?.toLowerCase().includes(searchText) ||
                                            facility.site_code?.toLowerCase().includes(searchText) ||
                                            facility.fac_code?.toLowerCase().includes(searchText) ||
                                            facility.located_region?.toLowerCase().includes(searchText)
                                        );
                                    }}
                                >
                                    {availableFacilities.map(facility => (
                                        <Option
                                            key={facility.id}
                                            value={facility.id}
                                            style={{
                                                backgroundColor: facility.isScheduled ? '#fffbe6' : 'white'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span>{facility.displayName}</span>
                                                {facility.isScheduled && (
                                                    <Tag color="warning" size="small">
                                                        {facility.scheduleCount}x
                                                    </Tag>
                                                )}
                                            </div>
                                            {facility.site_code && (
                                                <div style={{ fontSize: 11, color: '#888' }}>
                                                    Code: {facility.site_code} | Level: {facility.site_type} | Region: {facility.located_region}
                                                </div>
                                            )}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {selectedFacility && selectedFacility.isScheduled && (
                                <Alert
                                    message="Facility already scheduled"
                                    description={`This facility already has ${selectedFacility.scheduleCount} visit(s) on: ${selectedFacility.scheduledDates.map(d => moment(d).format('MMM DD')).join(', ')}`}
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            <Form.Item
                                label="Visit Date"
                                name="date"
                                rules={[{ required: true, message: 'Please select visit date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Day Number"
                                name="day_number"
                                rules={[{ required: true, message: 'Please enter day number' }]}
                            >
                                <Input type="number" min={1} placeholder="Day 1, Day 2, etc." />
                            </Form.Item>

                            <Form.Item
                                label="Visit Notes / Instructions"
                                name="notes"
                            >
                                <TextArea
                                    rows={4}
                                    placeholder="Add any special instructions or focus areas for this visit..."
                                    maxLength={500}
                                    showCount
                                />
                            </Form.Item>
                        </Form>
                    </TabPane>

                    {/* ── BULK SCHEDULE TAB ──────────────────────────────── */}
                    <TabPane tab="Bulk Schedule" key="bulk">
                        <Form form={form} layout="vertical">
                            <Alert
                                message="Bulk Scheduling"
                                description="Select multiple facilities and they will be scheduled on consecutive days starting from the selected date."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />

                            {/* ── NEW: Asset Type Selector for Bulk ── */}
                            <Form.Item
                                label="What are you collecting data for?"
                                name="bulk_asset_types"
                                rules={[{ required: true, message: 'Please select at least one asset type' }]}
                                extra="Applies to all visits in this bulk schedule"
                            >
                                <AssetTypeSelector
                                    value={form.getFieldValue('bulk_asset_types') || []}
                                    onChange={v => form.setFieldsValue({ bulk_asset_types: v })}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Select Facilities (in visit order)"
                                name="bulk_facilities"
                                rules={[{ required: true, message: 'Please select at least one facility' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select facilities in the order you want to visit them"
                                    onChange={setSelectedFacilities}
                                    disabled={availableFacilities.length === 0}
                                    filterOption={(input, option) => {
                                        const facility = availableFacilities.find(f => f.id === option.value);
                                        if (!facility) return false;
                                        const searchText = input.toLowerCase();
                                        return (
                                            facility.business_name?.toLowerCase().includes(searchText) ||
                                            facility.site_code?.toLowerCase().includes(searchText) ||
                                            facility.fac_code?.toLowerCase().includes(searchText) ||
                                            facility.located_region?.toLowerCase().includes(searchText)
                                        );
                                    }}
                                >
                                    {availableFacilities.map(facility => (
                                        <Option
                                            key={facility.id}
                                            value={facility.id}
                                            style={{
                                                backgroundColor: facility.isScheduled ? '#fffbe6' : 'white'
                                            }}
                                        >
                                            {facility.displayName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {selectedFacilities.length > 0 && (
                                <Alert
                                    message={`${selectedFacilities.length} facilities selected`}
                                    description={`Visits will be scheduled on ${selectedFacilities.length} consecutive days`}
                                    type="success"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            <Form.Item
                                label="Start Date"
                                name="bulk_start_date"
                                rules={[{ required: true, message: 'Please select start date' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    disabledDate={disabledDate}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Notes (applies to all visits)"
                                name="bulk_notes"
                            >
                                <TextArea
                                    rows={4}
                                    placeholder="Add notes that apply to all scheduled visits..."
                                    maxLength={500}
                                    showCount
                                />
                            </Form.Item>

                            {selectedFacilities.length > 0 && form.getFieldValue('bulk_start_date') && (
                                <div style={{
                                    padding: 12,
                                    background: '#f5f5f5',
                                    borderRadius: 4,
                                    marginTop: 16
                                }}>
                                    <strong>Schedule Preview:</strong>
                                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                        {selectedFacilities.map((facilityId, index) => {
                                            const facility = availableFacilities.find(f => f.id === facilityId);
                                            const visitDate = moment(form.getFieldValue('bulk_start_date')).add(index, 'days');
                                            return (
                                                <li key={facilityId}>
                                                    Day {index + 1} ({visitDate.format('DD MMM YYYY')}): {facility?.business_name}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </Form>
                    </TabPane>
                </Tabs>
            )}
        </Modal>
    );
};

export default VisitScheduleForm;